import { Link } from "react-router-dom";
import { Spinner } from "@heroui/react";
import {
  Buildings,
  Stack,
  TrendUp,
  CurrencyEur,
  CheckCircle,
  FileText,
  Briefcase,
  ChartLineIcon,
  SirenIcon,
} from "@phosphor-icons/react";
import { applyMask } from "shared/utils/masks";
import { cx } from "../utils/blockStyles.utils";
import {
  getUnpaidRentVariant,
  getOpexVariant,
  getContractsCreatedVariant,
  getAcquisitionsVariant,
  getTasksCompletedVariant,
} from "../../utils/metricThresholds";

interface MetricsBlockNewProps {
  keyFigures?: Record<string, any>;
  formattedOPEXValue: string;
  weeklyCompletedTasksByUser: number;
  weeklyCompletedTasksByEveryone: number;
  openTicketsCount?: {
    total: number;
    open: number;
    completion: number;
  } | null;
}

interface MetricCardProps {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  subtext?: string;
  link?: string;
  variant?: "default" | "primary" | "success" | "warning" | "error";
}

const MetricCard = ({
  icon,
  label,
  value,
  subtext,
  link,
  variant = "default",
}: MetricCardProps) => {
  const borderClasses = {
    default: "",
    primary: "border-2 border-moss-primary",
    success: "border-2 border-green-600",
    warning: "border-2 border-yellow-500",
    error: "border-2 border-red-500",
  };

  const numberColorClasses = {
    default: "text-foreground",
    primary: "text-neutral-primary",
    success: "text-green-700",
    warning: "text-yellow-700",
    error: "text-red-600",
  };

  const content = (
    <div
      className={cx(
        "bg-muted flex h-full flex-col rounded-lg p-3 shadow-sm",
        borderClasses[variant],
        link && "cursor-pointer",
      )}
    >
      <div className="mb-2 flex items-start justify-between gap-2">
        <div
          className={cx(
            "shrink-0 text-2xl leading-tight font-bold",
            numberColorClasses[variant],
          )}
        >
          {value}
        </div>
        <div className="text-muted-foreground shrink-0">{icon}</div>
      </div>
      <div className="flex flex-col">
        <div className="text-muted-foreground mb-1 text-xs font-semibold tracking-wide uppercase">
          {label}
        </div>
        {subtext && (
          <div className="text-foreground text-sm leading-tight font-medium">
            {subtext}
          </div>
        )}
      </div>
    </div>
  );

  if (link) {
    return (
      <Link to={link} className="block h-full">
        {content}
      </Link>
    );
  }

  return content;
};

export const MetricsBlockNew = ({
  keyFigures = {},
  formattedOPEXValue,
  weeklyCompletedTasksByUser,
  weeklyCompletedTasksByEveryone,
  openTicketsCount,
}: MetricsBlockNewProps) => {
  const safeKeyFigures = keyFigures ?? {};
  const {
    contractsCreatedThisYear,
    contractsCreatedThisYearValue,
    currentUnpaidRent,
    numBuildings,
    numUnits,
    nonChargeableOpexCosts,
    capexCosts,
    currentInvoicedRent,
    invoicedRentLines,
    acquisitionsInPipeline,
  } = safeKeyFigures;

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-4 flex items-center gap-2">
        <ChartLineIcon
          weight="duotone"
          size={20}
          className="text-muted-foreground"
        />
        <h2 className="text-foreground text-lg font-semibold">
          Belangrijkste cijfers
        </h2>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
        {/* Buildings */}
        <MetricCard
          icon={<Buildings weight="duotone" size={24} />}
          label="Gebouwen in eigendom"
          value={numBuildings ?? <Spinner size="sm" color="warning" />}
        />

        {/* Units */}
        <MetricCard
          icon={<Stack weight="duotone" size={24} />}
          label="Units in eigendom"
          value={numUnits ?? <Spinner size="sm" color="warning" />}
        />

        {/* Open Tickets */}
        <MetricCard
          icon={<SirenIcon weight="duotone" size={24} />}
          label="Openstaande tickets"
          value={
            openTicketsCount?.total ?? <Spinner size="sm" color="warning" />
          }
          subtext={
            openTicketsCount
              ? `Waarvan ${openTicketsCount.completion} in afronding`
              : undefined
          }
          link="/helpdesk"
          variant="default"
        />

        {/* Acquisitions Pipeline */}
        <MetricCard
          icon={<Briefcase weight="duotone" size={24} />}
          label="Acquisities in pipeline"
          value={
            acquisitionsInPipeline ?? <Spinner size="sm" color="warning" />
          }
          link="/acquisitions"
          variant={
            typeof acquisitionsInPipeline === "number"
              ? getAcquisitionsVariant(acquisitionsInPipeline)
              : "default"
          }
        />

        {/* Contracts Created This Year */}
        <MetricCard
          icon={<FileText weight="duotone" size={24} />}
          label="Nieuwe contracten (YTD)"
          value={
            contractsCreatedThisYear ?? <Spinner size="sm" color="warning" />
          }
          subtext={
            contractsCreatedThisYearValue
              ? applyMask(contractsCreatedThisYearValue, "valuta")
              : undefined
          }
          link="https://portal.yally.be/web#action=270&model=account.move&view_type=list&menu_id=291&cids=1%2C2%2C3%2C4"
          variant={
            typeof contractsCreatedThisYear === "number"
              ? getContractsCreatedVariant(contractsCreatedThisYear)
              : "default"
          }
        />

        {/* Invoiced Rent */}
        <MetricCard
          icon={<FileText weight="duotone" size={24} />}
          label="Klantenfacturen"
          value={
            typeof currentInvoicedRent === "number" ? (
              applyMask(currentInvoicedRent, "valuta")
            ) : (
              <Spinner size="sm" color="warning" />
            )
          }
          subtext={
            typeof invoicedRentLines === "number"
              ? `Waarvan ${applyMask(Math.abs(invoicedRentLines), "valuta")} huur`
              : undefined
          }
        />

        {/* Unpaid Rent */}
        <MetricCard
          icon={<CurrencyEur weight="duotone" size={24} />}
          label="Openstaande klantenfacturen"
          value={
            typeof currentUnpaidRent === "number" ? (
              applyMask(currentUnpaidRent, "valuta")
            ) : (
              <Spinner size="sm" color="warning" />
            )
          }
          link="https://portal.yally.be/web#action=270&model=account.move&view_type=list&menu_id=291&cids=1%2C2%2C3%2C4"
          variant={
            typeof currentUnpaidRent === "number"
              ? getUnpaidRentVariant(currentUnpaidRent)
              : "default"
          }
        />

        {/* OPEX */}
        <MetricCard
          icon={<TrendUp weight="duotone" size={24} />}
          label="OPEX kosten"
          value={
            typeof nonChargeableOpexCosts === "number" ? (
              applyMask(-nonChargeableOpexCosts, "valuta")
            ) : (
              <Spinner size="sm" color="warning" />
            )
          }
          subtext={`${formattedOPEXValue}% van geïnde huur`}
          variant={
            formattedOPEXValue
              ? getOpexVariant(parseFloat(formattedOPEXValue))
              : "default"
          }
        />

        {/* CAPEX */}
        <MetricCard
          icon={<CurrencyEur weight="duotone" size={24} />}
          label="CAPEX dit jaar"
          value={
            typeof capexCosts === "number" ? (
              applyMask(-capexCosts, "valuta")
            ) : (
              <Spinner size="sm" color="warning" />
            )
          }
        />

        {/* Tasks Completed */}
        <MetricCard
          icon={<CheckCircle weight="duotone" size={24} />}
          label="Taken deze week"
          value={`${weeklyCompletedTasksByUser}/${weeklyCompletedTasksByEveryone}`}
          subtext={`${weeklyCompletedTasksByUser} door jou`}
          link="/tasks"
          variant={getTasksCompletedVariant(weeklyCompletedTasksByUser)}
        />
      </div>
    </div>
  );
};
